import { useState } from "react";
import { ClipboardList, FileText, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { OpsNav } from "@/components/OpsNav";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { startLogin } from "@/const";

/**
 * FitLife-X fulfilment desk.
 *
 * The SOP rows carry the full questionnaire answers, because that is what you
 * are actually working from when you build the document - having to open a
 * second screen to read them would make this a list rather than a desk.
 */

const FORM_STATUSES = ["submitted", "in_progress", "delivered", "cancelled"] as const;
const SOP_STATUSES = ["submitted", "in_review", "awaiting_client", "delivered", "cancelled"] as const;
const AD_STATUSES = ["submitted", "quoted", "booked", "live", "completed", "declined"] as const;


/**
 * Sending an issue.
 *
 * Deliberately two buttons with different weights: a test send that goes to
 * Altitude and can be clicked freely, and a send to the list that requires
 * typing SEND. The second one cannot be undone by any button on this page,
 * which is the whole reason for the friction.
 */
function NewsletterSender() {
  const utils = trpc.useUtils();
  const status = trpc.fitlifeX.newsletterStatus.useQuery();
  const [slug, setSlug] = useState("");
  const [confirm, setConfirm] = useState("");

  const refresh = () => void utils.fitlifeX.newsletterStatus.invalidate();
  const onError = (error: { message: string }) => toast.error("That did not work", { description: error.message });

  const test = trpc.fitlifeX.sendNewsletterTest.useMutation({
    onSuccess: result =>
      result.status === "sent"
        ? toast.success("Test sent", { description: `Check ${result.to}.` })
        : toast.error("Test not delivered", { description: result.detail }),
    onError,
  });

  const send = trpc.fitlifeX.sendNewsletterToSubscribers.useMutation({
    onSuccess: result => {
      toast.success(`Sent to ${result.sent} subscriber${result.sent === 1 ? "" : "s"}.`, {
        description: result.failed ? `${result.failed} failed — see the counts below.` : undefined,
      });
      setConfirm("");
      refresh();
    },
    onError,
  });

  const issues = status.data?.issues ?? [];
  const selected = issues.find(issue => issue.slug === slug);

  return (
    <section className="section fx-send">
      <div className="site-container">
        <div className="fx-send__head">
          <Send size={18} />
          <div>
            <h2>Send a FitLife-X issue</h2>
            <p>
              Goes to subscribed members only, one at a time, each with their own unsubscribe link.
              An issue already sent to someone is never sent to them again, so re-running a
              half-finished send is safe.
            </p>
          </div>
          <p className="fx-send__count">
            <strong>{status.data?.subscribers ?? "—"}</strong>
            <span>subscribed</span>
            {Boolean(status.data?.unsubscribed) && <small>{status.data?.unsubscribed} opted out</small>}
          </p>
        </div>

        {issues.length === 0 && !status.isLoading && <p className="cp-empty">No issues are written yet.</p>}

        <div className="cp-desk__form-row">
          <label className="field">
            <span>Issue</span>
            <select value={slug} onChange={event => { setSlug(event.target.value); setConfirm(""); }}>
              <option value="">Select an issue…</option>
              {issues.map(issue => (
                <option key={issue.slug} value={issue.slug}>
                  {issue.number}. {issue.title} — {issue.publishOn}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Type SEND to confirm</span>
            <input value={confirm} onChange={event => setConfirm(event.target.value.toUpperCase())} placeholder="SEND" />
          </label>
        </div>

        {selected && (
          <p className="fx-send__state">
            Already delivered to <strong>{selected.sent}</strong> subscriber{selected.sent === 1 ? "" : "s"}
            {selected.failed > 0 && <>, <strong>{selected.failed}</strong> failed</>}.
          </p>
        )}

        <div className="fx-send__actions">
          <button
            type="button"
            className="button button--outline-dark"
            disabled={!slug || test.isPending}
            onClick={() => test.mutate({ slug })}
          >
            {test.isPending ? "Sending…" : "Send a test to Altitude"}
          </button>
          <button
            type="button"
            className="button button--coral"
            disabled={!slug || confirm !== "SEND" || send.isPending}
            onClick={() => send.mutate({ slug, confirm: "SEND" })}
          >
            {send.isPending ? "Sending…" : `Send to ${status.data?.subscribers ?? 0} subscribers`}
          </button>
        </div>

        {send.data?.failures?.length ? (
          <ul className="fx-send__failures">
            {send.data.failures.slice(0, 10).map(failure => (
              <li key={failure.email}><strong>{failure.email}</strong> {failure.detail}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

export default function FitLifeOperations() {
  const { user, loading } = useAuth();
  const queue = trpc.fitlifeX.queue.useQuery(undefined, { enabled: Boolean(user && user.role === "admin") });
  const utils = trpc.useUtils();
  const [openSop, setOpenSop] = useState<number | null>(null);

  const refresh = () => void utils.fitlifeX.queue.invalidate();
  const onError = (error: { message: string }) => toast.error("Status not changed", { description: error.message });

  const setFormStatus = trpc.fitlifeX.setFormStatus.useMutation({ onSuccess: refresh, onError });
  const setSopStatus = trpc.fitlifeX.setSopStatus.useMutation({ onSuccess: refresh, onError });
  const setAdStatus = trpc.fitlifeX.setAdStatus.useMutation({ onSuccess: refresh, onError });

  if (loading) return <SiteLayout><div className="ops-page"><OpsNav /><div className="site-container fx-ops-loading">Loading…</div></div></SiteLayout>;
  // Signed out and signed in without permission are different problems, and
  // only the first one has a button that helps. Collapsing them tells a
  // colleague who simply has not signed in that they lack permission.
  if (!user) {
    return (
      <SiteLayout>
        <div className="ops-page">
          <OpsNav />
          <section className="ops-access">
            <p className="ops-overline">Private workspace</p>
            <h1>Sign in to open the fulfilment desk.</h1>
            <button className="button button--coral" onClick={() => startLogin()}>Sign in</button>
          </section>
        </div>
      </SiteLayout>
    );
  }

  if (user.role !== "admin") {
    return (
      <SiteLayout>
        <div className="ops-page">
          <OpsNav />
          <section className="ops-access">
            <p className="ops-overline">Private workspace</p>
            <h1>This workspace is restricted.</h1>
            <p>Your account is authenticated but does not have FitLife-X administration permission.</p>
          </section>
        </div>
      </SiteLayout>
    );
  }

  const data = queue.data;

  return (
    <SiteLayout>
      <div className="ops-page">
        <OpsNav />
        <section className="ops-hero">
          <div className="site-container">
            <p className="eyebrow eyebrow--light">FitLife-X Pro</p>
            <h1>Fulfilment desk.</h1>
            <p>White-label form sets, SOP questionnaires and advertising enquiries from Pro members.</p>
          </div>
        </section>

        <NewsletterSender />

        <section className="section">
          <div className="site-container">
            {queue.isLoading && <p>Loading the queue…</p>}

            {data && (
              <>
                <FxGroup icon={FileText} title="Forms library" count={data.forms.length}>
                  {data.forms.map(row => (
                    <article key={row.id} className="fx-ops-row">
                      <div>
                        <strong>{row.brandName}</strong>
                        <small>{row.member.businessName || row.member.name || row.member.email} · {row.member.email}</small>
                        <p>{row.requestedForms.join(", ")}</p>
                        {row.brandColor && <p className="fx-ops-meta">Brand colour {row.brandColor}</p>}
                        {row.notes && <p className="fx-ops-meta">{row.notes}</p>}
                      </div>
                      <StatusPicker
                        value={row.status}
                        options={FORM_STATUSES}
                        pending={setFormStatus.isPending}
                        onChange={status => setFormStatus.mutate({ id: row.id, status })}
                      />
                    </article>
                  ))}
                </FxGroup>

                <FxGroup icon={ClipboardList} title="SOP development" count={data.sops.length}>
                  {data.sops.map(row => {
                    const answers = Object.entries(row.answers);
                    return (
                      <article key={row.id} className="fx-ops-row">
                        <div>
                          <strong>{row.model}</strong>
                          <small>{row.member.businessName || row.member.name || row.member.email} · {row.member.email}</small>
                          <p className="fx-ops-meta">{answers.length} answer{answers.length === 1 ? "" : "s"} supplied</p>
                          <button type="button" className="fx-ops-toggle" onClick={() => setOpenSop(openSop === row.id ? null : row.id)}>
                            {openSop === row.id ? "Hide answers" : "Show answers"}
                          </button>
                          {openSop === row.id && (
                            <dl className="fx-ops-answers">
                              {answers.map(([token, value]) => (
                                <div key={token}>
                                  <dt>{token}</dt>
                                  <dd>{value}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          {row.internalNotes && <p className="fx-ops-meta">Notes: {row.internalNotes}</p>}
                        </div>
                        <StatusPicker
                          value={row.status}
                          options={SOP_STATUSES}
                          pending={setSopStatus.isPending}
                          onChange={status => setSopStatus.mutate({ id: row.id, status })}
                        />
                      </article>
                    );
                  })}
                </FxGroup>

                <FxGroup icon={Megaphone} title="Advertising" count={data.ads.length}>
                  {data.ads.map(row => (
                    <article key={row.id} className="fx-ops-row">
                      <div>
                        <strong>{row.company}</strong>
                        <small>{row.member.email}{row.website ? ` · ${row.website}` : ""}</small>
                        <p>{row.productSummary}</p>
                        <p className="fx-ops-meta">
                          {row.placements.join(", ")}
                          {row.budgetBand ? ` · ${row.budgetBand}` : ""}
                          {row.startWindow ? ` · ${row.startWindow}` : ""}
                        </p>
                        {row.audienceFit && <p className="fx-ops-meta">{row.audienceFit}</p>}
                      </div>
                      <StatusPicker
                        value={row.status}
                        options={AD_STATUSES}
                        pending={setAdStatus.isPending}
                        onChange={status => setAdStatus.mutate({ id: row.id, status })}
                      />
                    </article>
                  ))}
                </FxGroup>
              </>
            )}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

function FxGroup({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof FileText;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="fx-ops-group">
      <header>
        <Icon size={16} />
        <h2>{title}</h2>
        <span>{count}</span>
      </header>
      {count === 0 ? <p className="fx-empty">Nothing here yet.</p> : <div>{children}</div>}
    </section>
  );
}

/**
 * Generic because the three streams have different status vocabularies, and a
 * single shared list would let a form request be marked "quoted".
 */
function StatusPicker<T extends string>({
  value,
  options,
  pending,
  onChange,
}: {
  value: string;
  options: readonly T[];
  pending: boolean;
  onChange: (status: T) => void;
}) {
  return (
    <label className="fx-ops-status">
      <span>Status</span>
      <select value={value} disabled={pending} onChange={event => onChange(event.target.value as T)}>
        {options.map(option => (
          <option key={option} value={option}>
            {option.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </label>
  );
}
